import type { ReactElement } from 'react';
import { useCallback, useMemo } from 'react';
import { Rect } from 'react-konva';
import type { FileResultResponse } from '../../../types/api.types';
import type { AnalysisTabs } from '../../../types/common.types';
import type { HandleToggleChangeProps } from '../../../types/canvas.types';
import {
  getDetectionOptions,
  getDetectionType,
  isDetectionClickable,
} from '@/utils/utils';
interface UseRenderDetectionsProps {
  resultData?: FileResultResponse;
  documentRatio: number;
  pageIndex: number;
  offset: { x: number; y: number };
  hideDetectionCanvas: boolean;
  hoverDetection: string | undefined;
  setHoverDetection: (hoverDetection: string | undefined) => void;
  handleToggleChange: HandleToggleChangeProps;
  selected2DDocId?: string;
  selectedMrzId?: string;
}

const useRenderDetections = ({
  resultData,
  documentRatio,
  pageIndex,
  offset,
  hideDetectionCanvas,
  hoverDetection,
  setHoverDetection,
  handleToggleChange,
  selected2DDocId,
  selectedMrzId,
}: UseRenderDetectionsProps) => {
  const renderDetectionsCanvas = useCallback(() => {
    if (!resultData?.detections || resultData.detections.length === 0) {
      return [];
    }

    const getColorDetection = (
      isGoodDetection: boolean,
      tab: AnalysisTabs | null,
      detectionId: string
    ) => {
      const baseColor = isGoodDetection
        ? 'rgba(0,255,0,0.8)'
        : 'rgba(255,0,0,0.8)';
      const baseColorOpacity = isGoodDetection
        ? 'rgba(0,255,0,0.4)'
        : 'rgba(255,0,0,0.4)';
      const baseColorMRZ = isGoodDetection
        ? 'rgba(0,255,0,0.2)'
        : 'rgba(255,0,0,0.2)';

      if (!hideDetectionCanvas) {
        // Pour le mrz, on veut pouvoir le lire donc on le rend transparent
        if (tab === 'mrz' && detectionId === selectedMrzId) {
          return baseColorMRZ;
        }

        // Sélection active
        if (tab === '2ddoc' && detectionId === selected2DDocId) {
          return baseColor;
        }

        // Hover actif
        if (hoverDetection === detectionId) {
          return baseColor;
        }

        return baseColorOpacity;
      }
      return 'transparent';
    };

    const currentDetections: ReactElement<{
      detectionId: string;
      type: AnalysisTabs;
    }>[] = [];
    const uniqueBboxes: string[] = [];

    resultData.detections.forEach((detection, topIndex) => {
      if (detection.location === null || detection.page !== pageIndex) {
        return;
      }
      const isGoodDetection = detection.is_good;
      const detectionType = getDetectionType(detection);
      const isClickable = isDetectionClickable(detection);
      const options = getDetectionOptions(detection);

      const location = detection.location;

      if (location === null || location.length === 0) {
        return;
      }
      const bbs = location;
      bbs.forEach((bbox, index) => {
        if (!uniqueBboxes.includes(bbox.join())) {
          uniqueBboxes.push(bbox.join());
          currentDetections.push(
            <Rect
              onMouseEnter={(e) => {
                setHoverDetection(detection.id);
                const container = e.target.getStage()?.container();
                if (container) {
                  if (isClickable) {
                    container.style.cursor = 'pointer';
                  } else {
                    container.style.cursor = 'default';
                  }
                }
              }}
              onMouseLeave={(e) => {
                setHoverDetection(undefined);
                const container = e.target.getStage()?.container();
                if (container) {
                  container.style.cursor = 'move';
                }
              }}
              onClick={() => {
                if (isClickable && detectionType) {
                  handleToggleChange(detectionType, options);
                }
              }}
              key={`${topIndex}${index}`}
              x={bbox[0] * documentRatio}
              y={bbox[1] * documentRatio}
              offsetX={offset.x}
              offsetY={offset.y}
              width={bbox[2] * documentRatio}
              height={bbox[3] * documentRatio}
              opacity={1}
              fill={getColorDetection(
                !!isGoodDetection,
                detectionType,
                detection.id
              )}
              type={detectionType}
              detectionId={detection.id}
            />
          );
        }
      });
    });

    return currentDetections;
  }, [
    resultData,
    documentRatio,
    offset,
    hideDetectionCanvas,
    hoverDetection,
    setHoverDetection,
    handleToggleChange,
    selected2DDocId,
    selectedMrzId,
    pageIndex,
  ]);

  return useMemo(() => renderDetectionsCanvas(), [renderDetectionsCanvas]);
};

export default useRenderDetections;
